//@flow
import axios from 'axios';
import queryString from 'query-string';
import { apiURL } from './config';
import authStore from './modules/auth/AuthStore';

const POST = 'post';
const GET = 'get';
const PUT = 'put';
const DELETE = 'delete';

const initApi = () => {
  const api = axios.create();
  api.defaults.baseURL = apiURL;

  return api;
};

const axiosApi = initApi();

const redirectOn401 = (error) => {
  if (error.response.data) {
    const { pathname, search } = window.location;
    let next = '';
    let params = search;
    if (!['', '/', '/login'].includes(pathname)) {
      next = `?next=${pathname}`;
      const paramObject = queryString.parse(search);
      if (params !== '') {
        params = `&${queryString.stringify(paramObject)}`;
      }
    }

    if (error.response.data.ssoUrl) {
      window.location.href = `${error.response.data.ssoUrl}${next}${params}`;
    }
    else {

      window.location.href = `/login${next}${params}`;
    }
  }
};

const getBody = (verb: string, body?: any): any => {
  // https://github.com/axios/axios/issues/897#issuecomment-343715381
  if (body !== undefined && verb === DELETE) {
    return { data: body };
  }

  return body;
};

const execute = async (verb: string, url: string, body?: any) => {
  try {
    const config = {
      url,
      method: verb,
      data: getBody(verb, body),
      withCredentials: true // include existing cookies as part of request header
    };
    const { status, data } = await axiosApi.request(config);

    // reset auth session expiry on successful api return
    authStore.resetExpiryDate();

    return {
      status,
      data
    };
  } catch (error) {
    const { status, data } = error.response;
    // handle 401s in the store, payload may contain relevant redirect info
    if (status === 401) {
      if (url === '/users/me') { // return 401 response on user fetch
        return {
          status,
          data
        };
      } else if (!window.allowAnonymous) { // if 401 on any other request other than user fetch, redirect to non-auth page
        redirectOn401(error);
      }
    }

    // CSC - for now re-throwing the error as all of the app code was written to catch exceptions...
    throw error;
    // return { status };
  }
};

class GWUApi {
  async login(username: string) {
    return execute(POST, '/login', { username, password: 'abcd' });
  }

  async logout() {
    return execute(GET, '/logout');
  }

  async getUserProfile() {
    return execute(GET, `/users/me?allowAnonymous=${window.allowAnonymous}`);
  }

  async updateUserProfile(updatePayload: Object) {
    return execute(PUT, '/users/me', updatePayload);
  }

  async keepAlive() {
    return execute(GET, '/auth/keepAlive');
  }

  async getUserRights(username: string) {
    return execute(GET, `/users/rights/${username}`);
  }

  async updateUserRights(username: string, rights: Object) {
    return execute(PUT, '/users/rights', { username, rights });
  }

  async getConfig() {
    return execute(GET, '/config');
  }

  async getLanguagesMeta() {
    return execute(GET, '/lang');
  }

  async getLanguage(code) {
    return execute(GET, `/lang/${code}`);
  }

  getDownloadLanguageLink(code) {
    return `${apiURL}/lang/csv/${code}`;
  }

  async uploadLanguage(formData) {
    return execute('POST', '/lang/csv', formData);
  }

  async getContent(type) {
    return execute(GET, `/content/${type}`);
  }

  async getSkills() {
    return execute(GET, '/skills');
  }

  async getUserSkills(username: string) {
    return execute(GET, `/skills/user/${username}`);
  }

  async addSkillForUser(username: string, skillId: number) {
    return execute(POST, '/skills/user', { username, skillId });
  }

  async removeSkillForUser(username: string, skillId: number) {
    return execute(DELETE, '/skills/user', { username, skillId });
  }

  async rateSkillForUser(username: string, skillId: number, rating: number) {
    return execute(PUT, '/skills/user', { username, skillId, rating });
  }

  async getResources() {
    return execute(GET, '/resources?include=["skills"]');
  }

  async getFeed() {
    return execute(GET, '/feed?include=["skills"]');
  }

  async getPathways() {
    return execute(GET, '/pathway?include=["topics"]&type=Regular');
  }

  async getCoachingPathways() {
    return execute(GET, '/pathway?include=["topics"]&type=Coaching');
  }

  async getPathway(pathwayId: number) {
    return execute(GET, `/pathway/${pathwayId}?include=["topics"]`);
  }

  async getPathwayByPath(path: string) {
    return execute(GET, `/pathway/path/${path}?include=["topics"]`);
  }

  async sendPathwaySuggestion(comment: string, commentType: string) {
    return execute(POST, `/pathway/suggestion`, { comment, commentType });
  }

  async getUserResourceActivities(resourceIds: Array<number>) {
    return execute(POST, '/resources/user/activities', { resourceIds });
  }

  async recordUserResourceActivity(resourceId: number, activity: string) {
    return execute(POST, '/resources/user/activity', { resourceId, activity });
  }

  async getStaticFileContent(filePath: string) {
    return fetch(filePath)
      .then(response => response.text())
      .then(text => {
        return text;
      });
  }
}

export default new GWUApi();



// WEBPACK FOOTER //
// ./src/api.js